import { fetchUserTickets } from "@/api-client/api/services/helpdesk/helpdeskService";
import {
  userStatus,
  UserStatusResponse,
} from "@/api-client/api/services/loginService/loginService";
import { createCtx } from "@/hooks/createCtx";
import {
  LOGIN_ROUTE,
  REGISTER_ROUTE,
  REGISTER_SUCCESS,
  REGISTER_VERIFICATION_PROCESS_ROUTE,
  REGISTRATION_VERIFICATION_ROUTE,
  RESET_PASSWORD_ROUTE,
} from "@/lib/constants/routes";
import { UserTicket } from "@/types/ticket";
import axios from "axios";
import React, { ReactNode, useCallback, useEffect, useState } from "react";

interface UnchekedTickets {
  user?: UserStatusResponse;
  uncheckedTickets: UserTicket[];
  loading: boolean;
  refreshUser: () => void;
  refreshUncheckedTickets: () => void;
  changeLanguage: (language: "en" | "ru" | "lv") => void;
}

const [useUserContext, UserProvider] = createCtx<UnchekedTickets>();

export { useUserContext };

export const UserContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [user, setUser] = useState<UserStatusResponse>();
  const [uncheckedTickets, setUncheckedTickets] = useState<UserTicket[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const location = window.location.pathname;
  const loadUncheckedTickets = useCallback(async () => {
    try {
      const tickets = await fetchUserTickets(false);
      setUncheckedTickets(tickets);
    } catch (error) {
      console.error("Error fetching user unchecked tickets: ", error);
    }
  }, []);

  const loadUserData = useCallback(async () => {
    try {
      setLoading(true);
      const user = await userStatus();
      setUser(user);
      loadUncheckedTickets();
    } catch (error) {
      if (!axios.isAxiosError(error) || error.status !== 403) {
        console.error("Error fetching user unchecked tickets: ", error);
      }
      setUser(undefined);
    } finally {
      setLoading(false);
    }
  }, [loadUncheckedTickets]);
  const changeLanguage = useCallback(async (language: string) => {
    setUser({ ...user, notification_language: language } as UserStatusResponse);
  }, []);

  useEffect(() => {
    if (
      location !== LOGIN_ROUTE &&
      location !== REGISTER_ROUTE &&
      location !== RESET_PASSWORD_ROUTE &&
      location !== REGISTER_VERIFICATION_PROCESS_ROUTE &&
      location !== REGISTRATION_VERIFICATION_ROUTE &&
      location !== REGISTER_SUCCESS
    ) {
      loadUserData();
    } else {
      setUser(undefined);
      setLoading(false);
    }
  }, [loadUserData, location]);

  return (
    <UserProvider
      value={{
        user,
        uncheckedTickets,
        refreshUser: loadUserData,
        refreshUncheckedTickets: loadUncheckedTickets,
        loading,
        changeLanguage,
      }}
    >
      {children}
    </UserProvider>
  );
};
